import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';
import type { RootState } from '@/store';

// Types mirroring backend resources
export interface Models {
  count: number; // between 0 and total responses
  sentiment_adjectives: string[];
  sentiment_score: number; // -1.0 .. 1.0
}

export interface ModelAnalysis {
  // companies[company] = Models
  companies: Record<string, Models>;
}

export interface BpOverview {
  id: string; // UUID4 of the buyer persona overview
  buyer_id: string; // UUID4 of the buyer persona associated to this overview
  // models[modelName] = ModelAnalysis
  models: Record<string, ModelAnalysis>;
  prompts: number; // total distinct prompts analyzed
  updated_at: string; // ISO datetime
}

export interface BpOverviewState {
  statusByDomain: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  errorByDomain: Record<string, string | undefined>;
  dataByDomain: Record<string, BpOverview[]>;
  /** Individual (non-aggregated) bp-overviews for time-series — one per persona per analysis run */
  individualByDomain: Record<string, BpOverview[]>;
  /** Tracks which `days` value is currently active per domain (set by syncBpDaysCache) */
  activeDaysByDomain: Record<string, number>;
}

const initialState: BpOverviewState = {
  statusByDomain: {},
  errorByDomain: {},
  dataByDomain: {},
  individualByDomain: {},
  activeDaysByDomain: {},
};

// Normalization helpers
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const asString = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

function toModels(v: unknown): Models | undefined {
  if (!isObj(v)) return undefined;
  const count = typeof v.count === 'number' ? v.count : undefined;
  const adjectives = Array.isArray(v.sentiment_adjectives)
    ? (v.sentiment_adjectives as unknown[]).map(x => (typeof x === 'string' ? x : undefined)).filter(Boolean) as string[]
    : undefined;
  const score = typeof v.sentiment_score === 'number' ? v.sentiment_score : undefined;
  if (typeof count !== 'number' || typeof score !== 'number') return undefined;
  return { count, sentiment_adjectives: adjectives ?? [], sentiment_score: score };
}

function toCompaniesMap(v: unknown): Record<string, Models> {
  if (!isObj(v)) return {};
  const inner: Record<string, Models> = {};
  for (const [company, m] of Object.entries(v)) {
    const mm = toModels(m);
    if (mm) inner[company] = mm;
  }
  return inner;
}

function toModelAnalysis(v: unknown): ModelAnalysis | undefined {
  if (!isObj(v)) return undefined;
  const companies = isObj(v.companies) ? toCompaniesMap(v.companies) : {};
  return { companies };
}

function toModelAnalysisDict(v: unknown): Record<string, ModelAnalysis> {
  if (!isObj(v)) return {};
  const out: Record<string, ModelAnalysis> = {};
  for (const [modelName, analysis] of Object.entries(v)) {
    const parsed = toModelAnalysis(analysis);
    if (parsed) out[modelName] = parsed;
  }
  return out;
}

function toBpOverview(v: unknown): BpOverview | undefined {
  if (!isObj(v)) return undefined;
  const id = asString(v.id); // overview id
  const buyer_id = asString(v.buyer_id); // persona id
  const prompts = typeof v.prompts === 'number' ? v.prompts : undefined;
  const updated_at = asString(v.updated_at);
  const models = toModelAnalysisDict(v.models);
  if (!id || !buyer_id || typeof prompts !== 'number' || !updated_at) return undefined;
  return { id, buyer_id, models, prompts, updated_at };
}

/** Parse raw API response into typed BpOverview array (re-usable outside the thunk).
 *  By default reads the `data` key (aggregated per-persona).
 *  Pass `key` to read a different field, e.g. `'bp_overviews'` for individual records. */
export function parseBpOverviews(payload: unknown, key: 'data' | 'bp_overviews' = 'data'): BpOverview[] {
  const src = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty(key))
    ? (payload as Record<string, unknown>)[key]
    : (key === 'data' ? payload : null);
  const arr = Array.isArray(src) ? src : [];
  return (arr as unknown[]).map(toBpOverview).filter(Boolean) as BpOverview[];
}

export const fetchBpOverviews = createAsyncThunk<
  { domainId: string; days: number; overviews: BpOverview[]; individual: BpOverview[] },
  { domainId: string; days?: number },
  { state: RootState }
>(
  'bpOverview/fetchBpOverviews',
  async ({ domainId, days = 7 }, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/bp-overviews`, { params: { days } });
      const payload: unknown = res.data;
      const data = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
        ? (payload as { data: unknown }).data
        : payload;
      const arr = Array.isArray(data) ? data : [];
      const overviews: BpOverview[] = (arr as unknown[])
        .map(toBpOverview)
        .filter(Boolean) as BpOverview[];
      // Parse individual (non-aggregated) bp-overviews for time-series
      const indRaw = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('bp_overviews'))
        ? (payload as { bp_overviews: unknown }).bp_overviews
        : null;
      const individual: BpOverview[] = Array.isArray(indRaw)
        ? (indRaw as unknown[]).map(toBpOverview).filter(Boolean) as BpOverview[]
        : [];
      return { domainId, days, overviews, individual };
    } catch (err: unknown) {
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      return rejectWithValue(typeof msg === 'string' ? msg : 'Failed to load buyer persona overviews');
    }
  },
  {
    condition: ({ domainId, days = 7 }: { domainId: string; days?: number }, { getState }) => {
      const { bpOverview } = getState();
      const ck = `${domainId}:${days}`;
      const st = bpOverview?.statusByDomain?.[ck];
      if (st === 'loading') return false;
      if (st === 'succeeded' && bpOverview?.dataByDomain?.[ck]) return false;
      return true;
    }
  }
);

const bpOverviewSlice = createSlice({
  name: 'bpOverview',
  initialState,
  reducers: {
    syncBpDaysCache(state, action: PayloadAction<{ domainId: string; days: number }>) {
      const { domainId, days } = action.payload;
      state.activeDaysByDomain[domainId] = days;
      const ck = `${domainId}:${days}`;
      if (state.dataByDomain[ck]) {
        state.dataByDomain[domainId] = state.dataByDomain[ck];
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
      }
      if (state.individualByDomain[ck]) {
        state.individualByDomain[domainId] = state.individualByDomain[ck];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBpOverviews.pending, (state, action) => {
        const { domainId, days = 7 } = action.meta.arg;
        const ck = `${domainId}:${days}`;
        state.statusByDomain[ck] = 'loading';
        state.errorByDomain[ck] = undefined;
        // Only mirror to simple key if this is the active time range
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          state.statusByDomain[domainId] = 'loading';
        }
      })
      .addCase(fetchBpOverviews.fulfilled, (state, action) => {
        const { domainId, days, overviews, individual } = action.payload;
        const ck = `${domainId}:${days}`;
        state.statusByDomain[ck] = 'succeeded';
        state.errorByDomain[ck] = undefined;
        state.dataByDomain[ck] = overviews;
        state.individualByDomain[ck] = individual;
        // Only mirror to simple key if this matches the active time range
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          state.statusByDomain[domainId] = 'succeeded';
          state.errorByDomain[domainId] = undefined;
          state.dataByDomain[domainId] = overviews;
          state.individualByDomain[domainId] = individual;
        }
      })
      .addCase(fetchBpOverviews.rejected, (state, action) => {
        const { domainId, days = 7 } = action.meta.arg;
        const ck = `${domainId}:${days}`;
        const msg = typeof action.payload === 'string' ? action.payload : 'Unknown error';
        state.statusByDomain[ck] = 'failed';
        state.errorByDomain[ck] = msg;
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          state.statusByDomain[domainId] = 'failed';
          state.errorByDomain[domainId] = msg;
        }
      });
  }
});

export const { syncBpDaysCache } = bpOverviewSlice.actions;
export default bpOverviewSlice.reducer;
